import { track, trackSubmit, getDataMontillaAttrs, getValidProperties, getAccessibleName } from './track'
import { page } from './page'
import { getCssSelector } from '../utils/dom-selector'

/**
 * Options for {@link setupAutoTrack}. Every field is optional with sensible
 * defaults that preserve the previous CDN `autoTrack` behaviour while moving
 * from per-element listeners to event delegation.
 */
export interface AutoTrackOptions {
  /**
   * CSS selector for click delegation. The nearest matching ancestor of the
   * click target is what gets tracked. Anchors / `[role=link]` emit
   * `link_interaction`; everything else emits an `<eventType>` interaction
   * with `element_*` attributes. Narrow this to e.g. `'[data-montilla-track]'`
   * to make tracking opt-in and keep event volume intentional.
   */
  clickSelector?: string
  /** Track form submits via a single delegated capture-phase listener. Default: `true`. */
  forms?: boolean
  /**
   * Impression tracking via a single `IntersectionObserver`. `true` uses the
   * defaults; pass an object to tune. Fires `section_view` once per element
   * when it crosses `threshold` visibility, then stops observing it.
   * Default: `true`.
   */
  impressions?: boolean | { selector?: string, threshold?: number, eventName?: string }
  /**
   * Defer setup to `requestIdleCallback` (fallback `setTimeout`) so it never
   * competes with page load / TTI. Default: `true`.
   */
  defer?: boolean
  /**
   * Auto-fire `montilla.page()` on SPA navigation — patches
   * `history.pushState`/`replaceState` and listens to `popstate`/`hashchange`,
   * plus one initial call for the landing route. This is what makes `page()`
   * "come with autoTrack" for a plain CDN SPA that has no router hook.
   *
   * Default: `true`. Set to `false` when something else already calls `page()`
   * — the Nuxt module does (on `page:finish`), so it passes `autoPage: false`
   * to avoid double-firing. `true` uses `location.pathname` as the route name;
   * pass `{ route }` to derive it yourself (e.g. include the search string).
   */
  autoPage?: boolean | { route?: () => string }
}

/** Handle returned by {@link setupAutoTrack}. */
export interface AutoTrackHandle {
  /**
   * (Re)scan and observe impression targets. Delegated click/submit listeners
   * are global and never need this, but impression targets added after a SPA
   * navigation do — call this on `page:finish` (or after rendering new content).
   */
  refresh: () => void
  /** Remove every listener + disconnect the observer. */
  destroy: () => void
}

const DEFAULT_CLICK_SELECTOR = [
  'a', '[role="link"]',
  'input', 'textarea', 'select', 'button', 'summary',
  '[role="button"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]',
  '[role="menuitem"]', '[role="option"]', '[role="tab"]', '[role="treeitem"]',
  '[data-montilla-track]',
].join(', ')

const LINK_SELECTOR = 'a, [role="link"]'

function isLink(el: Element): el is HTMLAnchorElement {
  return el instanceof HTMLAnchorElement || el.matches(LINK_SELECTOR)
}

/**
 * Mouse button + new-tab-intent modifier for a click-like event.
 * - `button`: '0' left · '1' middle · '2' right (empty for non-mouse events).
 * - `modifier`: 'true' when ctrl/meta is held (open-in-new-tab intent); empty
 *   otherwise, so getValidProperties omits it on plain clicks.
 */
function mouseInfo(event: Event): { button: string, modifier: string } {
  const me = event as MouseEvent
  const button = typeof me.button === 'number' ? String(me.button) : ''
  const modifier = me.metaKey || me.ctrlKey ? 'true' : ''
  return { button, modifier }
}

function runDeferred(fn: () => void, defer: boolean): void {
  if (!defer) return fn()
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
  if (ric) ric(fn)
  else setTimeout(fn, 0)
}

/**
 * Wire up delegated, low-overhead auto-tracking on the current document.
 *
 * Instead of attaching listeners to every link/element (O(N) listeners + a
 * full-DOM scan at load that also misses dynamically-added nodes), this binds
 * ONE capture-phase listener per event type on `document` and resolves the
 * tracked element via `closest()` at dispatch time. Cost is O(1) at setup,
 * scales with interactions (not DOM size), and works for SPA / lazy content
 * automatically — no `MutationObserver`, no re-scan on navigation.
 *
 * Safe to call on the server (no-ops without a `document`). Idempotent per
 * call: returns a handle to `refresh()` impressions and `destroy()` everything.
 */
export function setupAutoTrack(options: AutoTrackOptions = {}): AutoTrackHandle {
  const noop: AutoTrackHandle = { refresh: () => undefined, destroy: () => undefined }
  if (typeof document === 'undefined') return noop

  const {
    clickSelector = DEFAULT_CLICK_SELECTOR,
    forms = true,
    impressions = true,
    defer = true,
    autoPage = true,
  } = options

  const cleanups: (() => void)[] = []

  const handleClick = (eventType: string) => (event: Event) => {
    const target = event.target
    if (!(target instanceof Element)) return
    const el = target.closest(clickSelector)
    if (!el) return

    const mouse = mouseInfo(event)

    if (isLink(el)) {
      const link = el as HTMLAnchorElement
      void track('link_interaction', getValidProperties(
        {
          link_href: link.href ?? '',
          link_id: link.id,
          link_text: link.textContent ? link.textContent.replace(/[\n\t]/g, '').trim() : '',
          link_label: getAccessibleName(link),
          link_classes: link.getAttribute('class') ?? '',
          link_selector: getCssSelector(link),
          link_button: mouse.button,
          link_modifier: mouse.modifier,
        },
        getDataMontillaAttrs(link),
        { link_event: eventType, auto_track: 'true' },
      ))
      return
    }

    // Non-link interactive element. Forms are covered by the submit listener,
    // so skip elements living inside a <form> to avoid double-counting.
    if (el.closest('form')) return
    void track(eventType, getValidProperties(
      {
        element_id: el.id,
        element_type: el.tagName,
        element_text: el.textContent ? el.textContent.replace(/[\n\t]/g, '').trim() : '',
        element_label: getAccessibleName(el),
        element_classes: el.getAttribute('class') ?? '',
        element_selector: getCssSelector(el),
        element_button: mouse.button,
        element_modifier: mouse.modifier,
      },
      getDataMontillaAttrs(el),
      { element_event: eventType, auto_track: 'true' },
    ))
  }

  const bind = (type: string, listener: (e: Event) => void) => {
    document.addEventListener(type, listener, { capture: true })
    cleanups.push(() => document.removeEventListener(type, listener, { capture: true }))
  }

  // Anchors historically tracked click/auxclick/contextmenu; other elements
  // only click. A single delegated listener per type covers the whole document.
  bind('click', handleClick('click'))
  bind('auxclick', handleClick('auxclick'))
  bind('contextmenu', handleClick('contextmenu'))

  if (forms) {
    const onSubmit = (event: Event) => { void trackSubmit(event as SubmitEvent, { auto_track: 'true' }) }
    bind('submit', onSubmit)
  }

  // ── Impressions (single shared IntersectionObserver) ──
  let refresh = (): void => undefined
  const impressionsOn = impressions !== false
  if (impressionsOn && typeof IntersectionObserver !== 'undefined') {
    const cfg = typeof impressions === 'object' ? impressions : {}
    const selector = cfg.selector ?? 'section'
    const threshold = cfg.threshold ?? 0.5
    const eventName = cfg.eventName ?? 'section_view'
    const seen = new WeakSet<Element>()
    const observed = new WeakSet<Element>()

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold && !seen.has(entry.target)) {
          seen.add(entry.target)
          const el = entry.target as HTMLElement
          const all = Array.from(document.querySelectorAll(selector))
          const id = el.getAttribute('id') || String(all.indexOf(el))
          void track(eventName, getValidProperties(
            { section_id: id },
            getDataMontillaAttrs(el),
            { auto_track: 'true' },
          ))
          obs.unobserve(el)
        }
      })
    }, { threshold })
    cleanups.push(() => observer.disconnect())

    refresh = () => {
      // Skip nested matches (e.g. `section section`) to match the legacy
      // top-level-only behaviour; observe only targets not yet seen.
      const targets = Array.from(document.querySelectorAll(selector))
        .filter(el => !el.parentElement?.closest(selector) && !observed.has(el) && !seen.has(el))
      targets.forEach(el => { observed.add(el); observer.observe(el) })
    }
  }

  // ── Auto page views on SPA navigation (on by default) ──
  if (autoPage && typeof window !== 'undefined') {
    const routeFn = typeof autoPage === 'object' && autoPage.route
      ? autoPage.route
      : () => window.location.pathname

    let lastRoute: string | null = null
    const firePage = () => {
      let route = ''
      try { route = routeFn() } catch { /* resolver threw — skip */ }
      if (!route || route === lastRoute) return // dedupe same-route re-renders
      lastRoute = route
      void page(route)
    }

    // Patch history so programmatic SPA navigations (router.push) are seen —
    // pushState/replaceState don't emit events on their own.
    const origPush = window.history.pushState
    const origReplace = window.history.replaceState
    window.history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
      const ret = origPush.apply(this, args)
      firePage()
      return ret
    }
    window.history.replaceState = function (this: History, ...args: Parameters<History['replaceState']>) {
      const ret = origReplace.apply(this, args)
      firePage()
      return ret
    }
    const onNav = () => firePage()
    window.addEventListener('popstate', onNav)
    window.addEventListener('hashchange', onNav)
    cleanups.push(() => {
      window.history.pushState = origPush
      window.history.replaceState = origReplace
      window.removeEventListener('popstate', onNav)
      window.removeEventListener('hashchange', onNav)
    })

    // Initial page view for the landing route.
    firePage()
  }

  // Initial impression scan is deferred; delegated listeners are already live.
  runDeferred(refresh, defer)

  return {
    refresh,
    destroy: () => cleanups.splice(0).forEach(fn => fn()),
  }
}
